import { NotFound } from 'fejl'
import { type PayoutsApi } from '@/features/payouts/api/pro-api'
import { Id } from '@/infra/id'
import type { GetCurrentUserFn } from '@/features/iam'

/**
 * Looks up other users' payout methods.
 */
export class AdminPayoutMethodStore {
  constructor(
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _api?: PayoutsApi,
  ) {}

  /**
   * Fetches payout method for a user using BigMoney API.
   */
  async fetchPayoutMethodInfo(
    userPublicId: Id,
    signal?: AbortSignal,
  ): Promise<PayoutMethodInfo> {
    if (!this._api) {
      return { status: 'Unknown' }
    }

    if (this._getCurrentUser().platformAdmin) {
      return await this._api.payoutMethods
        .getByUserPublicId(Id.assert(userPublicId), signal)
        .then((method): PayoutMethodInfo => ({
          status: 'Connected',
          stripeAccountId: method.stripe_account_id,
        }))
        .catch(NotFound.ignore<PayoutMethodInfo>({ status: 'NotConnected' }))
    }

    return await this._api.payoutMethods
      .getStatusByUserPublicId(Id.assert(userPublicId), signal)
      .then(({ status }): PayoutMethodInfo => ({
        status: status === 'CONNECTED' ? 'Connected' : 'NotConnected',
        stripeAccountId: null,
      }))
  }
}

/**
 * Info about a user's payout method.
 */
export type PayoutMethodInfo =
  | { status: 'Connected'; stripeAccountId: string | null }
  | { status: 'NotConnected' }
  | { status: 'Unknown' }
